import { useDispatch } from 'react-redux';
import { useIsRevertAvailable, useRevertFlowMutation, wasRevertSuccessful } from 'automation-ui-canvas/queries/revertFlowQueries';
import { useFlpAccess } from 'automation-ui-permissions/flp/hooks/useFlpAccess';
import * as messageActions from 'automation-ui-canvas/actions/MessageActions';
import { UNDO_WORKFLOW_CHANGE_SUCCESS, UNDO_WORKFLOW_CHANGE_FAIL_GENERIC, REDO_WORKFLOW_CHANGE_SUCCESS, REDO_WORKFLOW_CHANGE_FAIL_GENERIC } from 'automation-ui-canvas/constants/AlertTypes';
import { useContext } from 'react';
import { UndoRedoContext } from 'automation-ui-canvas/containers/undo-redo/UndoRedoProvider';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { EDIT_ACTIONS } from 'automation-ui-permissions/feature-permissions/constants/WorkflowManagementFeatures';
import { jsx as _jsx } from "react/jsx-runtime";
const getUndoTooltipMessage = ({
  isRevertAvailable,
  revertError,
  hasFlpAccess
}) => {
  if (revertError === 'CURRENT_FLOW_REVISION_CONTAINS_EXTENSION_ACTION') {
    return /*#__PURE__*/_jsx(FormattedMessage, {
      message: "automation.common.undoRedo.disabledReasons.undo.CURRENT_FLOW_REVISION_CONTAINS_EXTENSION_ACTION"
    });
  }
  if (revertError === 'TARGET_FLOW_REVISION_CONTAINS_EXTENSION_ACTION') {
    return /*#__PURE__*/_jsx(FormattedMessage, {
      message: "automation.common.undoRedo.disabledReasons.undo.TARGET_FLOW_REVISION_CONTAINS_EXTENSION_ACTION"
    });
  }
  if (revertError === 'CURRENT_FLOW_REVISION_CONTAINS_CUSTOM_CODE_ACTION') {
    return /*#__PURE__*/_jsx(FormattedMessage, {
      message: "automation.common.undoRedo.disabledReasons.undo.CURRENT_FLOW_REVISION_CONTAINS_CUSTOM_CODE_ACTION"
    });
  }
  if (revertError === 'TARGET_FLOW_REVISION_CONTAINS_CUSTOM_CODE_ACTION') {
    return /*#__PURE__*/_jsx(FormattedMessage, {
      message: "automation.common.undoRedo.disabledReasons.undo.TARGET_FLOW_REVISION_CONTAINS_CUSTOM_CODE_ACTION"
    });
  }
  if (revertError === 'REACHED_END_OF_AVAILABLE_VERSION') {
    return /*#__PURE__*/_jsx(FormattedMessage, {
      message: "automation.common.undoRedo.disabledReasons.undo.REACHED_END_OF_AVAILABLE_VERSION"
    });
  }
  if (revertError === 'REVISION_DATA_OUT_OF_SYNC') {
    return /*#__PURE__*/_jsx(FormattedMessage, {
      message: "automation.common.undoRedo.disabledReasons.undo.REVISION_DATA_OUT_OF_SYNC"
    });
  }
  if (!isRevertAvailable) {
    return /*#__PURE__*/_jsx(FormattedMessage, {
      message: "automation.common.undoRedo.disabledReasons.undo.undoNotAvailable"
    });
  }
  if (!hasFlpAccess) {
    return /*#__PURE__*/_jsx(FormattedMessage, {
      message: "automation.common.undoRedo.disabledReasons.undo.noFlpAccess"
    });
  }
  return /*#__PURE__*/_jsx(FormattedMessage, {
    message: "automation.common.undoRedo.undoButtonLabel"
  });
};
const getRedoTooltipMessage = ({
  isRevertAvailable,
  revertError,
  hasFlpAccess
}) => {
  if (revertError === 'CURRENT_FLOW_REVISION_CONTAINS_EXTENSION_ACTION') {
    return /*#__PURE__*/_jsx(FormattedMessage, {
      message: "automation.common.undoRedo.disabledReasons.redo.CURRENT_FLOW_REVISION_CONTAINS_EXTENSION_ACTION"
    });
  }
  if (revertError === 'TARGET_FLOW_REVISION_CONTAINS_EXTENSION_ACTION') {
    return /*#__PURE__*/_jsx(FormattedMessage, {
      message: "automation.common.undoRedo.disabledReasons.redo.TARGET_FLOW_REVISION_CONTAINS_EXTENSION_ACTION"
    });
  }
  if (revertError === 'CURRENT_FLOW_REVISION_CONTAINS_CUSTOM_CODE_ACTION') {
    return /*#__PURE__*/_jsx(FormattedMessage, {
      message: "automation.common.undoRedo.disabledReasons.redo.CURRENT_FLOW_REVISION_CONTAINS_CUSTOM_CODE_ACTION"
    });
  }
  if (revertError === 'TARGET_FLOW_REVISION_CONTAINS_CUSTOM_CODE_ACTION') {
    return /*#__PURE__*/_jsx(FormattedMessage, {
      message: "automation.common.undoRedo.disabledReasons.redo.TARGET_FLOW_REVISION_CONTAINS_CUSTOM_CODE_ACTION"
    });
  }
  if (revertError === 'REACHED_END_OF_AVAILABLE_VERSION') {
    return /*#__PURE__*/_jsx(FormattedMessage, {
      message: "automation.common.undoRedo.disabledReasons.redo.REACHED_END_OF_AVAILABLE_VERSION"
    });
  }
  if (revertError === 'REVISION_DATA_OUT_OF_SYNC') {
    return /*#__PURE__*/_jsx(FormattedMessage, {
      message: "automation.common.undoRedo.disabledReasons.redo.REVISION_DATA_OUT_OF_SYNC"
    });
  }
  if (!isRevertAvailable) {
    return /*#__PURE__*/_jsx(FormattedMessage, {
      message: "automation.common.undoRedo.disabledReasons.redo.redoNotAvailable"
    });
  }
  if (!hasFlpAccess) {
    return /*#__PURE__*/_jsx(FormattedMessage, {
      message: "automation.common.undoRedo.disabledReasons.undo.noFlpAccess"
    });
  }
  return /*#__PURE__*/_jsx(FormattedMessage, {
    message: "automation.common.undoRedo.redoButtonLabel"
  });
};
export const useUndoRedo = ({
  requestType,
  flowId,
  currentVersion
}) => {
  const dispatch = useDispatch();
  const undoRedoContext = useContext(UndoRedoContext);
  const {
    data: flpData,
    loading: flpDataLoading
  } = useFlpAccess(flowId);
  const variables = {
    currentVersion,
    flowId,
    requestType
  };
  const {
    isRevertAvailable,
    revertError
  } = useIsRevertAvailable({
    variables
  });
  const setIsLoading = requestType === 'UNDO' ? undoRedoContext.setIsUndoLoading : undoRedoContext.setIsRedoLoading;
  const isLoading = flpDataLoading || (requestType === 'UNDO' ? undoRedoContext.isUndoLoading : undoRedoContext.isRedoLoading);
  const hasFlpAccess = !!(flpData !== null && flpData !== void 0 && flpData.hasAccess(EDIT_ACTIONS));
  const [onClick, {
    data
  }] = useRevertFlowMutation({
    variables,
    onRevertSuccess: () => {
      dispatch(messageActions.addAlert(requestType === 'UNDO' ? UNDO_WORKFLOW_CHANGE_SUCCESS : REDO_WORKFLOW_CHANGE_SUCCESS));
      setIsLoading(false);
    },
    onRevertError: () => {
      dispatch(messageActions.addAlert(requestType === 'UNDO' ? UNDO_WORKFLOW_CHANGE_FAIL_GENERIC : REDO_WORKFLOW_CHANGE_FAIL_GENERIC));
      setIsLoading(false);
    },
    onError: () => {
      dispatch(messageActions.addAlert(requestType === 'UNDO' ? UNDO_WORKFLOW_CHANGE_FAIL_GENERIC : REDO_WORKFLOW_CHANGE_FAIL_GENERIC));
      setIsLoading(false);
    }
  });
  const tooltipMessage = requestType === 'UNDO' ? getUndoTooltipMessage({
    isRevertAvailable,
    revertError,
    hasFlpAccess
  }) : getRedoTooltipMessage({
    isRevertAvailable,
    revertError,
    hasFlpAccess
  });
  const revertFailed = !wasRevertSuccessful(data);
  const isDisabled = isLoading || !isRevertAvailable || !hasFlpAccess;
  return {
    isDisabled,
    tooltipMessage,
    onClick: () => {
      setIsLoading(true);
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      onClick();
    },
    revertFailed,
    isLoading
  };
};